// Environment utility functions

export type DataMode = 'empty' | 'mock' | 'api';

export interface EnvVars {
  VITE_API_URL: string;
  VITE_APP_NAME: string;
  VITE_APP_VERSION: string;
  VITE_APP_ENVIRONMENT: string;
  VITE_ENABLE_DEV_TOOLS: string;
  VITE_ENABLE_ANALYTICS: string;
  VITE_ENABLE_ERROR_REPORTING: string;
  VITE_ENABLE_VIDEO: string;
  VITE_POSTHOG_KEY: string;
  VITE_POSTHOG_HOST: string;
  VITE_SENTRY_DSN: string;
  VITE_DATA_MODE: string;
}

// Type-safe environment variable access
export function getEnvVar(key: keyof EnvVars): string {
  const value = import.meta.env[key];
  return value || '';
}

// Get environment variable with default
export function getEnvVarWithDefault(
  key: keyof EnvVars,
  defaultValue: string
): string {
  const value = import.meta.env[key];
  return value || defaultValue;
}

// Get boolean environment variable
export function getBooleanEnvVar(key: keyof EnvVars): boolean {
  const value = import.meta.env[key];
  return value === 'true';
}

// Check if we're in a specific environment
export function isDevelopment(): boolean {
  return import.meta.env.MODE === 'development';
}

export function isStaging(): boolean {
  return import.meta.env.VITE_APP_ENVIRONMENT === 'staging';
}

export function isProduction(): boolean {
  return import.meta.env.MODE === 'production';
}

// Check if testing
export function isTesting(): boolean {
  return import.meta.env.MODE === 'test';
}

// Environment configuration validation
export function validateEnvironment(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  const requiredVars: (keyof EnvVars)[] = [
    'VITE_API_URL',
    'VITE_APP_NAME',
    'VITE_APP_VERSION',
  ];

  requiredVars.forEach(varName => {
    if (!getEnvVar(varName)) {
      errors.push(`Missing required environment variable: ${varName}`);
    }
  });

  // Validate URL format
  const apiUrl = getEnvVar('VITE_API_URL');
  if (apiUrl) {
    try {
      new URL(apiUrl);
    } catch {
      errors.push('VITE_API_URL must be a valid URL');
    }
  }

  // Validate version format
  const version = getEnvVar('VITE_APP_VERSION');
  if (version && !/^\d+\.\d+\.\d+(-\w+)?$/.test(version)) {
    errors.push(
      'VITE_APP_VERSION must follow semantic versioning (e.g., 1.0.0)'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Get data mode from environment
//
// Reads `import.meta.env.VITE_DATA_MODE` as a STATIC member expression, not via
// `getEnvVar('VITE_DATA_MODE')`. This is load-bearing, not style: `getEnvVar`
// does `import.meta.env[key]` with a computed key, and Vite's env replacement
// only substitutes static member accesses. With the computed form the value
// stays a runtime lookup, so every `if (isMock)` in the app is a runtime branch
// and Rollup cannot treat any mock-data import as dead. That single indirection
// is why ~5k lines of demo fixtures were reaching production bundles.
//
// Written as a static read here, `VITE_DATA_MODE` is inlined as a literal at
// build time, so `isMockDataMode()` folds to `false` in a production build and
// the async loaders in `@/data` drop out entirely.
export function getDataMode(): DataMode {
  const mode = import.meta.env.VITE_DATA_MODE || '';
  const validModes: DataMode[] = ['empty', 'mock', 'api'];

  if (validModes.includes(mode as DataMode)) {
    return mode as DataMode;
  }

  // Default to mock in development, api in production
  return isDevelopment() ? 'mock' : 'api';
}

// Data mode helper functions
export function isEmptyDataMode(): boolean {
  return getDataMode() === 'empty';
}

export function isMockDataMode(): boolean {
  return getDataMode() === 'mock';
}

export function isApiDataMode(): boolean {
  return getDataMode() === 'api';
}

// Print environment information (dev only)
export function printEnvironmentInfo(): void {
  if (!isDevelopment()) return;

  // Environment info logging removed for production
}
